import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Plus, Save, X } from "lucide-react";
import { api, fmtMoney, formatErr } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { useInvoiceAttachmentDraft } from "./useInvoiceAttachmentDraft";
import { InvoiceAttachmentEditor } from "./InvoiceAttachmentEditor";

const emptyLine = () => ({ source_index: null, date: "", category: "", vendor: "", description: "", amount: "" });

export const InvoiceEditDialog = ({ request, onOpenChange, onSaved, endpointBase = "/mgmt/invoices", allowAttachments = false }) => {
  const invoice = request?.invoice;
  const [invoiceDate, setInvoiceDate] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const [persisted, setPersisted] = useState(null);
  const [saveError, setSaveError] = useState("");
  const attachments = useInvoiceAttachmentDraft({ open: !!invoice, invoiceId: invoice?.id, enabled: allowAttachments });

  useEffect(() => {
    if (!invoice) return;
    setInvoiceDate(invoice.invoice_date || "");
    setNotes(invoice.notes || "");
    setPersisted(null); setSaveError("");
    setItems((invoice.line_items || []).map((line, index) => ({
      source_index: index,
      date: line.date || "",
      category: line.category || "",
      vendor: line.vendor || "",
      description: line.description || "",
      amount: String(line.amount ?? ""),
    })));
  }, [invoice]);

  const setItem = (index, patch) => setItems((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item));
  const subtotal = useMemo(() => items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0), [items]);
  const markupPct = Number(invoice?.markup_pct || 0);
  const total = Math.round(subtotal * (1 + markupPct / 100) * 100) / 100;
  const amountPaid = Number(invoice?.amount_paid || 0);

  const save = async () => {
    if (inFlight.current) return;
    const lineItems = items.filter((item) => item.description.trim() || Number(item.amount));
    if (!invoiceDate) return toast.error("Invoice date is required.");
    if (!lineItems.length) return toast.error("Add at least one line item.");
    if (total + 0.005 < amountPaid) return toast.error(`Invoice total cannot be less than the ${fmtMoney(amountPaid)} already received.`);
    inFlight.current = true; setSaving(true);
    let saved = persisted;
    try {
      const { data } = saved ? { data: saved } : await api.put(`${endpointBase}/${invoice.id}`, {
        invoice_date: invoiceDate,
        notes: notes.trim(),
        edit_reason: request.reason || "",
        line_items: lineItems.map((item) => ({ ...item, amount: Number(item.amount) || 0 })),
      });
      saved = data; setPersisted(saved); setSaveError("");
      await attachments.commit(invoice.id);
      toast.success(`${invoice.number} updated. No email sent.`);
      onSaved(data);
    } catch (error) {
      if (saved) setSaveError("Invoice changes are saved, but attachment changes have not finished. Retry to finish them. No email was sent.");
      toast.error(formatErr(error.response?.data?.detail) || error.message);
    } finally { inFlight.current = false; setSaving(false); }
  };

  if (!invoice) return null;
  const close = () => {
    if (inFlight.current) return;
    if (persisted) onSaved?.(persisted, { attachmentsPending: true });
    onOpenChange(false);
  };
  return (
    <Dialog open={!!invoice} onOpenChange={value => { if (!value) close(); }}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="invoice-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit invoice {invoice.number}</DialogTitle>
          <DialogDescription>Update dates, charges, and notes. Invoice number, colony, branding, payments, and source links stay protected.</DialogDescription>
        </DialogHeader>
        {(invoice.sent_at || ["sent", "paid"].includes(invoice.status)) && <div className="flex gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="invoice-edit-sent-warning"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>This invoice was already sent. Reason: <strong>{request.reason}</strong>. Saving does not resend it.</span></div>}
        <fieldset disabled={saving || !!persisted} className="min-w-0 space-y-4 disabled:opacity-70">
          <div className="max-w-xs"><Label>Invoice date</Label><Input type="date" value={invoiceDate} onChange={(event) => setInvoiceDate(event.target.value)} data-testid="invoice-edit-date" /></div>
          <div>
            <Label>Charges</Label>
            <div className="mt-1 space-y-2">
              {items.map((item, index) => (
                <div key={`${item.source_index ?? "new"}-${index}`} className="space-y-2 rounded-sm border border-border p-3" data-testid={`invoice-edit-line-${index}`}>
                  <div className="flex gap-2"><Input className="flex-1" placeholder="Description" value={item.description} onChange={(event) => setItem(index, { description: event.target.value })} data-testid={`invoice-edit-line-description-${index}`} /><Input className="w-28" type="number" step="0.01" placeholder="Amount" value={item.amount} onChange={(event) => setItem(index, { amount: event.target.value })} data-testid={`invoice-edit-line-amount-${index}`} /><Button type="button" size="icon" variant="ghost" className="shrink-0 text-destructive" onClick={() => setItems((current) => current.filter((_, itemIndex) => itemIndex !== index))} aria-label={`Remove charge ${index + 1}`} data-testid={`invoice-edit-line-remove-${index}`}><X className="h-4 w-4" /></Button></div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-3"><Input type="date" value={item.date} onChange={(event) => setItem(index, { date: event.target.value })} aria-label={`Charge ${index + 1} date`} data-testid={`invoice-edit-line-date-${index}`} /><Input placeholder="Category" value={item.category} onChange={(event) => setItem(index, { category: event.target.value })} data-testid={`invoice-edit-line-category-${index}`} /><Input placeholder="Vendor" value={item.vendor} onChange={(event) => setItem(index, { vendor: event.target.value })} data-testid={`invoice-edit-line-vendor-${index}`} /></div>
                </div>
              ))}
              <Button type="button" size="sm" variant="outline" onClick={() => setItems((current) => [...current, emptyLine()])} data-testid="invoice-edit-add-line"><Plus className="mr-2 h-4 w-4" />Add charge</Button>
            </div>
          </div>
          <div><Label>Note on invoice</Label><Textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} data-testid="invoice-edit-notes" /></div>
          <div className="space-y-1 border-t border-border pt-3 text-sm" data-testid="invoice-edit-totals"><div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="mono-num">{fmtMoney(subtotal)}</span></div>{markupPct > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Existing markup ({markupPct}%)</span><span className="mono-num">{fmtMoney(total - subtotal)}</span></div>}<div className="flex justify-between font-bold"><span>Updated total</span><span className="mono-num">{fmtMoney(total)}</span></div>{amountPaid > 0 && <div className="flex justify-between text-emerald-700"><span>Already received</span><span className="mono-num">−{fmtMoney(amountPaid)}</span></div>}</div>
        </fieldset>
        {allowAttachments && <InvoiceAttachmentEditor draft={attachments} invoiceId={invoice.id} disabled={saving} id="edit-invoice-attachments" />}
        {saveError && <p role="alert" className="text-sm text-destructive" data-testid="invoice-edit-attachment-save-error">{saveError}</p>}
        <DialogFooter><Button variant="outline" disabled={saving} onClick={close} data-testid="invoice-edit-cancel">{persisted ? "Finish later" : "Cancel"}</Button><Button onClick={save} disabled={saving || attachments.loading} data-testid="invoice-edit-save"><Save className="mr-2 h-4 w-4" />{saving ? "Saving…" : persisted ? "Retry attachments" : "Save changes"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};